import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import { showSideMenu } from "../../../../../App";
import Agents from "../../../../API/Agents";
import Files from "../../../../API/Files";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import { isMobileScreen } from "../../../MainViewContainer";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import QaPreSelection from "./QaPreSelection";

const QaConfigList = ({ isQaRolesLoading }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const [isLoadingInProgress, setIsLoadingInProgress] = useState(true);
  const [lastHiredAgentId, setLastHiredAgentId] = useState(null);
  const [showGoToChatChoice, setShowGoToChatChoice] = useState(false);
  const [minimisedTable, setMinisedTable] = useState(false);
  const [baseAgents, setBaseAgents] = useState([]);
  const [hireableAgents, setHireableAgents] = useState([]);
  const generateMeanigfulAgentName = (agent) => {
    if (agent?.agentType === "Chatbot") {
      if (agent?.mode === "chatter") {
        return t("settings.list.assistant");
      }
      if (agent?.mode === "coder") {
        return t("settings.list.programmer");
      }
      if (agent?.mode === "retriever") {
        return t("settings.list.operator");
      }
      if (agent?.mode === "computer") {
        return t("settings.list.desktop");
      }
      if (agent?.mode === "planner") {
        return t("settings.list.orchestrator");
      }
      if (agent?.mode === "voice") {
        return t("settings.list.voice");
      } else {
        return t("settings.list.operator");
      }
    } else if (agent?.agentType === "ContentCreator") {
      return t("settings.list.contentCreator");
    } else if (agent?.agentType === "BusinessAnalyst") {
      return t("settings.list.businessAnalyst");
    } else {
      return t("settings.list.helper");
    }
  };
  const {
    getActiveWorkspace,
    getPlanConfig,
    loadAgents,
    getWorkspaceEntities,
  } = useWorkspaces();

  const agentGroupById = useMemo(() => {
    const topics = (getWorkspaceEntities?.("topic") || []).filter(
      (e) => !e?._deleted,
    );
    const topicById = {};
    topics.forEach((topic) => {
      topicById[topic.id] = topic;
    });
    const map = {};
    state?.agents?.forEach((agent) => {
      const topic = agent?.agentCategoryType
        ? topicById[agent.agentCategoryType]
        : null;
      if (topic) {
        map[agent.id] = {
          label: topic?.label,
          color: topic?.backgroundColor,
          emoji: topic?.emoji,
        };
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.agents, getWorkspaceEntities?.("topic")?.length]);
  const [showPreselectionModal, setShowPreselectionModal] = React.useState(
    false,
  );

  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedAgentId, setSelectedAgentId] = React.useState(null);
  const [displayedRoles, setDisplayedRoles] = React.useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = React.useState([]);
  const AppToasty = AppToast.useToast();
  const { width, height } = useWindowDimensions();

  const isAgentsThreasholdReached = () => {
    return state?.agents?.length >= getPlanConfig()?.maxAgents;
  };
  useEffect(() => {
    fetchBaseAgents();
  }, [getActiveWorkspace()?.id]);
  const fetchBaseAgents = async () => {
    try {
      setIsLoadingInProgress(true);
      const resultBaseAgents = await Agents.getBaseAgentsByWorkspaceID("root");
      const resultHireableAgents = await Agents.getBaseAgentsByWorkspaceID(
        "hireable",
      );
      setBaseAgents(resultBaseAgents?.data?.agentsByWorkspace?.items);
      setHireableAgents(resultHireableAgents?.data?.agentsByWorkspace?.items);
    } catch (error) {
      console.log("Error while loading agents", error);
    } finally {
      setIsLoadingInProgress(false);
    }
  };
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (optId === "hire") {
      setShowPreselectionModal(true);
      return;
    }
    history.replace(`/workspaces/${wsId}/agents/${optId}`);
  }, [params?.optId, getActiveWorkspace()?.id]);
  useEffect(() => {
    setMinisedTable(width < 1124);
  }, [width]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.3,
    },
    {
      title: t("settings.list.description"),
    },
    {
      title: t("settings.list.mode", "Mode"),
      width: 0.2,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
    },
  ];
  const handleDuplication = async (id, clonedAgentID = null) => {
    try {
      if (isAgentsThreasholdReached()) {
        AppToasty.show(t("agentsThreasholdErrorCaption"), {
          type: "danger",
        });
        return;
      }
      let item;
      if (clonedAgentID)
        item = hireableAgents?.find((u) => u.id == clonedAgentID);
      else item = state?.agents?.find((u) => u.id === id);
      let newRole = {
        ...item,
        label: `${item.label}${clonedAgentID ? "" : " - Copy"}`,
        workspaceID: getActiveWorkspace()?.id,
        clonedAgentID,
      };
      delete newRole.id;
      delete newRole.createdAt;
      delete newRole.updatedAt;
      delete newRole._version;
      delete newRole._deleted;
      delete newRole._lastChangedAt;
      delete newRole._typename;
      if (newRole?.parentAgentID) delete newRole?.parentAgentID;
      if (newRole?.parentChatID) delete newRole?.parentChatID;

      const creationResult = await Agents.createAgent(newRole, user?.id);
      loadAgents(getActiveWorkspace()?.id);
      if (clonedAgentID)
        AppToasty.show(t("qaHiredSuccessCaption"), {
          type: "success",
        });
      else
        AppToasty.show(t("qaDuplicationSuccessCaption"), {
          type: "success",
        });
      setShowPreselectionModal(false);
      return creationResult?.data?.createAgent?.id;
    } catch (error) {
      if (clonedAgentID)
        AppToasty.show(t("qaHiredErrorCaption"), {
          type: "danger",
        });
      else
        AppToasty.show(t("qaDuplicationErrorCaption"), {
          type: "danger",
        });
      setShowPreselectionModal(false);
      console.log("error", error);
    } finally {
      setShowPreselectionModal(false);
    }
  };
  const handleCreation = async (id, label, aiConfig = null) => {
    setShowPreselectionModal(false);
    setSelectedAgentId(null);
    setSelectedIndex(null);
    if (id == "new") {
      history.push(`/workspaces/${getActiveWorkspace()?.id}/agents/new`, {
        aiConfig: aiConfig || null,
        label,
      });
    } else {
      const last_agent_id = await handleDuplication(id, id);
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/agents/${last_agent_id}`,
      );
    }
  };
  const SelectionQaConfigView = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          height: "95%",
          maxWidth: "95%",
          width: isMobileScreen() ? "99%" : "90%",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        key={"pre_selection"}
        isModalOpen={showPreselectionModal}
        setIsModalOpen={setShowPreselectionModal}
        modalTitle={t("settings.list.newAgent")}
        closeModal={() => {
          setShowPreselectionModal(false);
          history.push(
            `/workspaces/${getActiveWorkspace()?.id}/settings/roles_settings`,
          );
        }}
      >
        <QaPreSelection
          setShowPreselectionModal={setShowPreselectionModal}
          agents={hireableAgents}
          handleCreation={handleCreation}
        />
      </ToothFairyModal>
    );
  }, [showPreselectionModal, hireableAgents?.length, baseAgents?.length]);
  useEffect(() => {
    if (state?.agents) {
      setDisplayedRoles(state?.agents);
    }
  }, [JSON.stringify(state?.agents)]);

  const handleRoleItemeDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedAgentId || selectedItem?.id;
      if (_id) {
        // delete item from the list at the index of the selected item and create a new array
        const deletionResult = await Agents.deleteAgent(_id);
        const newAgents = state?.agents?.filter((agent) => agent.id !== _id);
        dispatch("SET_AGENTS", [...newAgents]);
        loadAgents(getActiveWorkspace()?.id);

        AppToasty.show(t("qaDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedAgentId(null);
        setSelectedItem(null);
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      console.log("error", error);
      AppToasty.show(t("qaDeletionFailedCaption"), {
        type: "danger",
      });
    }
  };

  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const handleBulkDeletion = async () => {
    try {
      setShowBulkDeleteChoice(false);
      for (const id of selectedAgentIds) {
        await Agents.deleteAgent(id);
      }
      const newAgents = state?.agents?.filter(
        (agent) => !selectedAgentIds.includes(agent.id),
      );
      dispatch("SET_AGENTS", [...(newAgents || [])]);
      loadAgents(getActiveWorkspace()?.id);
      setSelectedAgentIds([]);
      AppToasty.show(t("qaDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log("error", error);
      AppToasty.show(t("qaDeletionFailedCaption"), {
        type: "danger",
      });
    }
  };
  const _bulkDeleteChoice = (
    <ToothFairyChoice
      cancelCaption={t("settings.list.cancelConfirmation")}
      caption={t(
        "settings.list.deleteAgentsConfirmation",
        "Delete selected agents?",
      )}
      subCaption={t(
        "settings.list.deleteAgentSubConfirmation",
        "This action cannot be undone.",
      )}
      confirmCaption={t("settings.list.deleteAgent")}
      onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
      onConfirmButtonPress={handleBulkDeletion}
      setShowChoice={setShowBulkDeleteChoice}
      showChoice={showBulkDeleteChoice}
    />
  );

  const [agentIcons, setAgentIcons] = useState({});
  useEffect(() => {
    const loadAgentIcons = async () => {
      const wsId = getActiveWorkspace()?.id;
      if (!wsId || !displayedRoles?.length) return;
      const pageItems = displayedRoles;
      const updates = {};
      await Promise.all(
        pageItems.map(async (u) => {
          if (!u?.icon) return;
          try {
            const res = await Files.downloadUrlGeneration({
              filename: `imported-image/${wsId}/${u.icon}`,
              context: "pdf",
              workspaceid: wsId,
            });
            const url = res?.data?.url || res?.url;
            if (url) updates[u.id] = url;
          } catch {}
        }),
      );
      if (Object.keys(updates).length > 0)
        setAgentIcons((prev) => ({ ...prev, ...updates }));
    };
    loadAgentIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(displayedRoles?.map((u) => u?.id + u?.icon)),
    getActiveWorkspace()?.id,
  ]);

  const handleGoToChat = () => {
    history.push(
      `/workspaces/${
        getActiveWorkspace()?.id
      }/chats/newchat_${lastHiredAgentId}`,
    );
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteAgentConfirmation")}
        subCaption={t("settings.list.deleteAgentSubConfirmation")}
        confirmCaption={t("settings.list.deleteAgent")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={handleRoleItemeDeletion}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [
    setShowChoice,
    showChoice,
    selectedItem,
    setSelectedItem,
    selectedIndex,
    selectedAgentId,
    handleRoleItemeDeletion,
  ]);

  const _goToChatChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.goToChatCancelConfirmation")}
        caption={t("settings.list.goToChatCaption")}
        subCaption={t("settings.list.goToChatSubCaption")}
        confirmCaption={t("settings.list.goToChat")}
        onCancelButtonPress={() => setShowGoToChatChoice(false)}
        onConfirmButtonPress={handleGoToChat}
        setShowChoice={setShowGoToChatChoice}
        showChoice={showGoToChatChoice}
      />
    );
  }, [
    setShowGoToChatChoice,
    showGoToChatChoice,
    selectedItem,
    setSelectedItem,
    selectedIndex,
    handleGoToChat,
  ]);

  const isEnabledToOperate = (item) => {
    if (
      (state?.agents?.find((u) => u?.id == item?.id)?.agentType ==
        "ContentCreator" &&
        !getPlanConfig()?.isContentCreatorEnabled) ||
      (state?.agents?.find((u) => u?.id == item?.id)?.agentType ==
        "BusinessAnalyst" &&
        !getPlanConfig()?.isBusinessAnalystEnabled)
    ) {
      AppToasty.show(t("toasts.agents.notEnabledModify"), {
        type: "danger",
      });
      return false;
    } else return true;
  };
  const shortenName = (agentType) => {
    if (agentType == "ContentCreator") return "CC";
    else if (agentType == "BusinessAnalyst") return "BA";
    else if (agentType == "Orchestrator") return "PL";
    else return "AG";
  };
  return (
    <View
      style={{
        minWidth: "100%",
        flex: 1,
        width: width - (showSideMenu() ? 220 : 0),
        paddingLeft: 10,
        overflow: isLoadingInProgress && "hidden",
      }}
    >
      {isLoadingInProgress ? (
        <ToothFairyLoadingPlaceholder
          caption={t("settings.list.loadingAgents")}
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible={true}
          style={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
            marginHorizontal: "auto",
          }}
        />
      ) : (
        <>
          {displayedRoles?.length > 0 ? (
            <AppTable
              isSelectable
              showPagination
              itemsPerPage={50}
              hideSearch={false}
              searchPlaceholder={t("settings.toolbar.searchQaConfig")}
              suppressInTableFields={["imageUrl", "group", "chipColors"]}
              onSelectionChange={(selectedItems) => {
                setSelectedAgentIds(selectedItems?.map((u) => u?.id) || []);
              }}
              bulkActions={[
                {
                  id: "DELETE",
                  label: t("settings.list.delete"),
                  icon: AppAssets.icons.delete,
                  isDestructive: true,
                },
              ]}
              onBulkActionPress={(id) => {
                if (id === "DELETE" && selectedAgentIds?.length > 0)
                  setShowBulkDeleteChoice(true);
              }}
              isItemDeletable={(item) => {
                _item = state?.items;
                return true;
              }}
              isItemEditable={(item) => {
                return true;
              }}
              isItemActionable={(item) => {
                return true;
              }}
              marginRightBeforeActions={30}
              actionButtons
              handleDocumentSelection={(item) => {
                if (
                  isEnabledToOperate(item) &&
                  state?.agents?.findIndex((u) => u?.id === item?.id) != -1
                ) {
                  history.push(
                    `/workspaces/${getActiveWorkspace()?.id}/agents/${item.id}`,
                  );
                }
              }}
              handleDocumentDeletion={(item, index) => {
                if (isEnabledToOperate(item)) {
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setSelectedAgentId(item?.id);
                  setShowChoice(true);
                }
              }}
              mainActionLabel={t("settings.list.view")}
              showDropDownMenuForActions
              onClickMainAction={(item, index) => {
                if (
                  isEnabledToOperate(item) &&
                  state?.agents?.findIndex((u) => u?.id === item?.id) != -1
                ) {
                  history.push(
                    `/workspaces/${getActiveWorkspace()?.id}/agents/${item.id}`,
                  );
                }
              }}
              actionButtonStyle={{
                minWidth: 20,
              }}
              onActionItemPress={(id, item, index) => {
                if (id === "EDIT") {
                  if (
                    isEnabledToOperate(item) &&
                    state?.agents?.findIndex((u) => u?.id === item?.id) != -1
                  ) {
                    history.push(
                      `/workspaces/${getActiveWorkspace()?.id}/agents/${
                        item.id
                      }`,
                    );
                  }
                } else if (id === "DELETE") {
                  if (isEnabledToOperate(item)) {
                    setSelectedItem(item);
                    setSelectedIndex(index);
                    setSelectedAgentId(item?.id);
                    setShowChoice(true);
                  }
                } else if (id === "DUPLICATE") {
                  if (isEnabledToOperate(item)) {
                    handleDuplication(item?.id);
                  }
                } else if (id == "CHAT") {
                  history.push(
                    `/workspaces/${getActiveWorkspace()?.id}/chats/newchat_${
                      item.id
                    }`,
                  );
                }
              }}
              actions={[
                {
                  label: t("settings.list.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("settings.list.chat"),
                  id: "CHAT",
                  icon: AppAssets.icons.chatIcon,
                },
                {
                  label: t("settings.list.duplicate"),
                  id: "DUPLICATE",
                  icon: AppAssets.icons.duplicate,
                },
                {
                  label: t("settings.list.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              style={{
                zIndex: -1,
                marginTop: 15,
                height:
                  displayedRoles?.length >= 50 ? height - 75 : height - 55,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              headers={columns}
              items={displayedRoles?.map((u) => {
                if (
                  !u?.isGlobal ||
                  (u?.isGlobal &&
                    getPlanConfig()?.isBusinessAnalystEnabled &&
                    u?.agentType == "BusinessAnalyst") ||
                  (getPlanConfig()?.isContentCreatorEnabled &&
                    u?.agentType == "ContentCreator") ||
                  u?.agentType == "Chatbot"
                )
                  return {
                    id: u.id,
                    label: u?.label
                      ? u?.agentVersion
                        ? `${u.label} (v${u.agentVersion})`
                        : u.label
                      : " ",
                    description:
                      u?.description ||
                      t("settings.list.noDescriptionProvided"),
                    agentType: minimisedTable
                      ? shortenName(u.agentType)
                      : generateMeanigfulAgentName(u),
                    imageUrl: agentIcons[u.id] || null,
                    group: agentGroupById[u.id]?.label || null,
                    chipColors: agentGroupById[u.id]?.color
                      ? { group: agentGroupById[u.id].color }
                      : undefined,
                    actions: " ",
                  };
              })}
            >
              <ToothFairyButton
                title={t("settings.toolbar.hireAgent")}
                onPress={() => {
                  setShowPreselectionModal(true);
                }}
              />
            </AppTable>
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "100%",
                marginVertical: 20,
                marginHorizontal: "auto",
                flex: 1,
                zIndex: -1,
              }}
              size={30}
              icon={AppAssets.icons.taskAgent}
              mainCaption={t("settings.list.noAgentsFound")}
              subCaption={t("settings.list.noAgentsFoundSub")}
              actionLabel={t("settings.toolbar.hireAgent")}
              onActionPress={() => setShowPreselectionModal(true)}
            />
          )}
        </>
      )}
      {SelectionQaConfigView}
      {_coreChoice}
      {_bulkDeleteChoice}
      {_goToChatChoice}
    </View>
  );
};

export default QaConfigList;
